import { lazy, Suspense, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import type { RouteObject } from "react-router-dom";
import NotFound from "../pages/NotFound";
import LoginPage from "../pages/login/page";
import DashboardPage from "../pages/dashboard/page";
import ClientsPage from "../pages/clients/page";
import JobCardsPage from "../pages/jobcards/page";
import NewJobCardPage from "../pages/jobcards/new/page";
import JobCardDetailPage from "../pages/jobcards/detail/page";
import BillingPage from "../pages/billing/page";
import QuotationsPage from "../pages/billing/quotations/page";
import NewQuotationPage from "../pages/billing/quotation/page";
import PaymentsPage from "../pages/billing/payments/page";
import NewInvoicePage from "../pages/billing/invoice/page";
import ReportsPage from "../pages/reports/page";
import EmployeesPage from "../pages/employees/page";
import EmployeeDetailPage from "../pages/employees/detail/page";
import LeaveCalendarPage from "../pages/employees/leave-calendar/page";
import TeamAvailabilityPage from "../pages/employees/availability/page";
import FieldAppPage from "../pages/fieldapp/page";
import ClientPortalPage from "../pages/clientportal/page";
import NotificationsPage from "../pages/notifications/page";
import DeliverablesPage from "../pages/deliverables/page";

// ── Lazy-loaded chunks (heavy / rarely-used pages) ───────────────────────────
const PrintJobCardPage          = lazy(() => import("../pages/jobcards/print/page"));
const PrintQuotationPage        = lazy(() => import("../pages/billing/quotation/print/page"));
const PrintInvoicePage          = lazy(() => import("../pages/billing/invoice/print/page"));
const FieldTrackingPage         = lazy(() => import("../pages/fieldtracking/page"));

// Admin pages — lazy-loaded as a group (code-split from main bundle)
const AdminOverviewPage         = lazy(() => import("../pages/admin/page"));
const AdminUsersPage            = lazy(() => import("../pages/admin/users/page"));
const AdminRolesPage            = lazy(() => import("../pages/admin/roles/page"));
const AdminAuditLogsPage        = lazy(() => import("../pages/admin/audit-logs/page"));
const AdminSettingsPage         = lazy(() => import("../pages/admin/settings/page"));
const AdminDatabasePage         = lazy(() => import("../pages/admin/database/page"));
const AdminNotificationsPage    = lazy(() => import("../pages/admin/notifications-config/page"));
const SiteVisitRequestsPage     = lazy(() => import("../pages/admin/site-visits/page"));
const MediaPage                 = lazy(() => import("../pages/admin/media/page"));
const ServicesManagementPage    = lazy(() => import("../pages/admin/services/page"));

// ── Shared page loader ───────────────────────────────────────────────────────
function PageLoader(): ReactNode {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#fafaf8" }}>
      <div className="flex items-center gap-3" style={{ color: "#92400e" }}>
        <div
          className="w-5 h-5 border-2 rounded-full animate-spin"
          style={{ borderColor: "#fde68a", borderTopColor: "#f59e0b" }}
        />
        <span className="text-sm font-medium">Loading...</span>
      </div>
    </div>
  );
}

// ── Helper to wrap lazy pages ────────────────────────────────────────────────
function L({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

const routes: RouteObject[] = [
  // Auth
  { path: "/",      element: <LoginPage /> },
  { path: "/login", element: <LoginPage /> },

  // Core ERP pages (eagerly loaded — fast navigation)
  { path: "/dashboard",                    element: <DashboardPage /> },
  { path: "/clients",                      element: <ClientsPage /> },
  { path: "/job-cards",                    element: <JobCardsPage /> },
  { path: "/job-cards/new",                element: <NewJobCardPage /> },
  { path: "/job-cards/:id",                element: <JobCardDetailPage /> },
  { path: "/billing",                      element: <BillingPage /> },
  { path: "/billing/quotations",           element: <QuotationsPage /> },
  { path: "/billing/payments",             element: <PaymentsPage /> },
  { path: "/billing/quotation/new",        element: <NewQuotationPage /> },
  { path: "/billing/invoice/new",          element: <NewInvoicePage /> },
  { path: "/reports",                      element: <ReportsPage /> },
  { path: "/settings",                     element: <Navigate to="/admin/settings" replace /> },
  { path: "/employees",                    element: <EmployeesPage /> },
  { path: "/employees/:id",                element: <EmployeeDetailPage /> },
  { path: "/employees/leave-calendar",     element: <LeaveCalendarPage /> },
  { path: "/employees/availability",       element: <TeamAvailabilityPage /> },
  { path: "/field-app",                    element: <FieldAppPage /> },
  { path: "/client-portal",               element: <ClientPortalPage /> },
  { path: "/notifications",               element: <NotificationsPage /> },
  { path: "/deliverables",                element: <DeliverablesPage /> },

  // Print pages (lazy — opened in new tab, not part of main flow)
  { path: "/job-cards/:id/print",          element: <L><PrintJobCardPage /></L> },
  { path: "/billing/quotation/:id/print",  element: <L><PrintQuotationPage /></L> },
  { path: "/billing/invoice/:id/print",    element: <L><PrintInvoicePage /></L> },

  // Field tracking (lazy — heavy map component)
  { path: "/field-tracking",              element: <L><FieldTrackingPage /></L> },

  // Admin panel (lazy — admin-only, rarely visited by most users)
  { path: "/admin",                        element: <L><AdminOverviewPage /></L> },
  { path: "/admin/users",                  element: <L><AdminUsersPage /></L> },
  { path: "/admin/roles",                  element: <L><AdminRolesPage /></L> },
  { path: "/admin/audit-logs",             element: <L><AdminAuditLogsPage /></L> },
  { path: "/admin/settings",              element: <L><AdminSettingsPage /></L> },
  { path: "/admin/database",              element: <L><AdminDatabasePage /></L> },
  { path: "/admin/notifications-config",  element: <L><AdminNotificationsPage /></L> },
  { path: "/admin/site-visits",           element: <L><SiteVisitRequestsPage /></L> },
  { path: "/media",                        element: <L><MediaPage /></L> },
  { path: "/admin/services",               element: <L><ServicesManagementPage /></L> },

  { path: "*", element: <NotFound /> },
];

export default routes;
